import type { FormatableInterval, Interval } from '../../types';
import {
  dateAdd,
  dateDiff,
  dateSubtract,
  dateMin,
  dateStartOf,
  dateFormat,
  dateMonthIndex,
  monthYearToDate,
  getMonthIndex,
  getYear,
  dateIsBefore,
} from './dateFns';
import {
  dateToFiscalQuarterString,
  dateToFiscalYearString,
} from './fiscalFormats';
import type { unitOfTime } from 'moment';

/*
 * Helper classes for working with dates in the scale bar (and their
 * pixel/percentage equivalents). Generally, values should be kept as
 * dates for as long as possible (until it's time to render them onto
 * the screen); performing multiple conversions between dates and other
 * units can introduce subtle rounding errors and inaccuracies.
 */

export const getPixelsPerDay = ({
  startDate,
  endDate,
  width,
  hideWeekends = false,
}) => {
  const days = dateDiff(startDate, endDate, { business: hideWeekends });
  return width / days;
};

const intervalOptions: Record<string, unitOfTime.DurationConstructor[]> = {
  days: ['quarters', 'months', 'days'],
  weeks: ['quarters', 'months', 'weeks'],
  months: ['quarters', 'months'],
  quarters: ['years', 'quarters'],
};

export const getNormalizedIntervals = params => {
  const { visibleStartDate, visibleEndDate } = params;
  const { visibleWidth, hideWeekends = false, timescale = 'auto' } = params;

  const pixelsPerDay = getPixelsPerDay({
    startDate: visibleStartDate,
    endDate: visibleEndDate,
    width: visibleWidth,
    hideWeekends,
  });

  let intervals: unitOfTime.DurationConstructor[] = [];
  if (timescale === 'auto') {
    if (Math.round(pixelsPerDay) >= 18) {
      intervals = intervalOptions.days;
    } else if (pixelsPerDay > 3) {
      intervals = intervalOptions.weeks;
    } else if (pixelsPerDay > 1.25) {
      intervals = intervalOptions.months;
    } else {
      intervals = intervalOptions.quarters;
    }
  } else {
    if (['months', 'weeks', 'days'].includes(timescale) && pixelsPerDay < 1.2) {
      intervals = intervalOptions.quarters;
    } else if (['weeks', 'days'].includes(timescale) && pixelsPerDay < 2.1) {
      intervals = intervalOptions.months;
    } else if (timescale === 'days' && pixelsPerDay < 16) {
      intervals = intervalOptions.weeks;
    } else {
      intervals = intervalOptions[timescale];
    }
  }

  return intervals;
};

export const getInterval = (key, params): FormatableInterval => {
  switch (key) {
    case 'days':
      return getDayInterval(params);
    case 'weeks':
      return getWeekInterval(params);
    case 'months':
      return getMonthInterval(params);
    case 'quarters':
      return getQuarterInterval(params);
    case 'years':
      return getYearInterval(params);
    default:
      return getWeekInterval(params);
  }
};

const getDayInterval = ({
  startDate,
  endDate,
  hideWeekends,
}): FormatableInterval => {
  return {
    intervals: generateIntervals({
      startDate,
      endDate,
      startOfFirstInterval: startDate,
      hideWeekends,
      getNextDate: date => dateAdd(date, 1, { business: hideWeekends }),
    }) as [Interval],
    format: date => {
      const day = dateFormat(date, 'dd');
      if (day === 'Th') {
        return day;
      }
      return day[0];
    },
    totalDays: calculateTotalDays({ startDate, endDate, hideWeekends }),
  };
};

const getWeekInterval = ({
  startDate,
  endDate,
  hideWeekends,
}): FormatableInterval => {
  return {
    intervals: generateIntervals({
      startDate,
      endDate,
      startOfFirstInterval: dateStartOf(
        startDate,
        hideWeekends ? 'isoweek' : 'week'
      ),
      hideWeekends,
      getNextDate: date => dateAdd(date, 1, { period: 'week' }),
    }) as [Interval],
    format: (date, type) => dateFormat(date, type === 'major' ? 'MMM D' : 'D'),
    totalDays: calculateTotalDays({ startDate, endDate, hideWeekends }),
  };
};

const getMonthInterval = ({
  startDate,
  endDate,
  hideWeekends,
}): FormatableInterval => {
  return {
    intervals: generateIntervals({
      startDate,
      endDate,
      startOfFirstInterval: dateStartOf(startDate, 'month'),
      hideWeekends,
      getNextDate: date => dateAdd(date, 1, { period: 'month' }),
    }) as [Interval],
    format: (date, type) =>
      dateFormat(
        date,
        type === 'major' && dateMonthIndex(date) === 0 ? 'YYYY MMM' : 'MMM'
      ),
    totalDays: calculateTotalDays({ startDate, endDate, hideWeekends }),
  };
};

const getQuarterInterval = ({
  startDate,
  endDate,
  hideWeekends,
  fiscalMonth,
  fiscalYearFormat,
}): FormatableInterval => {
  // go back until we find a month that aligns with the fiscal month
  let startOfFirstInterval = dateStartOf(startDate, 'month');
  const fiscalMonthIndex = fiscalMonth - 1;

  while ((dateMonthIndex(startOfFirstInterval) - fiscalMonthIndex) % 3 !== 0) {
    startOfFirstInterval = dateSubtract(startOfFirstInterval, 1, {
      period: 'month',
    });
  }

  return {
    intervals: generateIntervals({
      startDate,
      endDate,
      startOfFirstInterval,
      hideWeekends,
      getNextDate: date => dateAdd(date, 3, { period: 'months' }),
    }) as [Interval],
    // Fiscal years starting month is configurable causing quarters not to
    // represent 'real' dates like other intervals like years, months, etc.
    // Set quarter year based on when fiscal year ends instead of the
    // quarter's year.

    format: date =>
      dateToFiscalQuarterString(date, {
        fiscalMonth,
        format: fiscalYearFormat,
      }),
    totalDays: calculateTotalDays({ startDate, endDate, hideWeekends }),
  };
};

const getYearInterval = ({
  startDate,
  endDate,
  hideWeekends,
  fiscalMonth,
  fiscalYearFormat,
}): FormatableInterval => {
  const fiscalMonthIndex = fiscalMonth - 1;

  // year intervals must be based on the fiscal year start and displayed in the chosen
  // fiscal year format
  return {
    intervals: generateIntervals({
      startDate,
      endDate,
      // based on generateInterval logic, if the startDate is before the fiscalMonth
      // in the year, need to have the first interval start before the startDate,
      // otherwise, it skips the start of the first fiscal year
      startOfFirstInterval:
        getMonthIndex(startDate) < fiscalMonthIndex
          ? dateSubtract(
              monthYearToDate(getYear(startDate), fiscalMonthIndex),
              1,
              { period: 'year' }
            )
          : monthYearToDate(getYear(startDate), fiscalMonthIndex),
      hideWeekends,
      getNextDate: date => dateAdd(date, 1, { period: 'year' }),
    }) as [Interval],
    format: date =>
      dateToFiscalYearString(date, {
        fiscalMonth,
        format: fiscalYearFormat,
      }),
    totalDays: calculateTotalDays({ startDate, endDate, hideWeekends }),
  };
};

// Generates the intervals in the form of a start date and a number of days included in the interval
// starting from startDate and ending on endDate.
//
// startOfFirstInterval is used to calculate the start of each interval
// because startDate might be in the middle of an interval
const generateIntervals = ({
  startDate,
  endDate,
  startOfFirstInterval,
  hideWeekends,
  getNextDate,
}): Interval[] => {
  let startOfInterval = startDate;
  let endOfInterval = getNextDate(startOfFirstInterval);

  const intervals: { startOfInterval: any; daysCount: any }[] = [];
  while (dateIsBefore(startOfInterval, endDate)) {
    const daysCount = dateDiff(startOfInterval, endOfInterval, {
      business: hideWeekends,
    });

    intervals.push({ startOfInterval, daysCount });

    startOfInterval = endOfInterval;
    endOfInterval = dateMin(getNextDate(startOfInterval), endDate);
  }

  return intervals;
};

const calculateTotalDays = ({ startDate, endDate, hideWeekends }) => {
  return dateDiff(startDate, endDate, { business: hideWeekends });
};
